import { z } from 'zod'

/**
 * Metrics, alert rules and notification channels (PROMPT.md §5.8).
 *
 * Timestamps in a series are epoch seconds rather than ISO strings: a 7-day graph is a few
 * thousand points, and the frontend feeds them straight into a chart anyway.
 */

export const diskUsageSchema = z.object({
  mount: z.string(),
  used: z.number().nonnegative(),
  size: z.number().nonnegative(),
  inodesUsedPct: z.number().min(0).max(100).nullable(),
})
export type DiskUsage = z.infer<typeof diskUsageSchema>

export const metricPointSchema = z.object({
  /** Epoch seconds. */
  ts: z.number().int().nonnegative(),
  cpuPct: z.number().min(0).max(100),
  memUsed: z.number().nonnegative(),
  memTotal: z.number().nonnegative(),
  swapUsed: z.number().nonnegative().nullable(),
  swapTotal: z.number().nonnegative().nullable(),
  disks: z.array(diskUsageSchema),
  load1: z.number().nonnegative().nullable(),
  load5: z.number().nonnegative().nullable(),
  load15: z.number().nonnegative().nullable(),
})
export type MetricPoint = z.infer<typeof metricPointSchema>

export const metricRangeSchema = z.enum(['1h', '24h', '7d'])
export type MetricRange = z.infer<typeof metricRangeSchema>

export const metricsQuerySchema = z.object({
  range: metricRangeSchema.default('1h'),
})

export const metricsSeriesSchema = z.object({
  serverId: z.string(),
  range: metricRangeSchema,
  /** Which table answered: raw samples, or the hourly rollup for the long ranges. */
  resolution: z.enum(['raw', 'hourly']),
  points: z.array(metricPointSchema),
})
export type MetricsSeries = z.infer<typeof metricsSeriesSchema>

/** What the poller pushes after every tick, for the live tiles. */
export const liveSampleSchema = z.object({
  serverId: z.string(),
  at: z.iso.datetime(),
  cpuPct: z.number().min(0).max(100).nullable(),
  memUsedPct: z.number().min(0).max(100).nullable(),
  /** Fullest mount, which is the one that ruins the day. */
  diskUsedPct: z.number().min(0).max(100).nullable(),
  diskMount: z.string().nullable(),
  load1: z.number().nonnegative().nullable(),
  uptimeSec: z.number().nonnegative().nullable(),
})
export type LiveSample = z.infer<typeof liveSampleSchema>

// ---------------------------------------------------------------- alerts

export const alertKindSchema = z.enum([
  'server_unreachable',
  'disk_above',
  'mem_above',
  'cpu_above_for',
  'process_not_running',
  'ssl_expires_in',
  'updates_pending_above',
])
export type AlertKind = z.infer<typeof alertKindSchema>

/**
 * Wie jede Regelart heißt. Liegt hier und nicht in der Oberfläche, weil das Lagebild seine
 * Überschriften im Backend baut — zwei Listen derselben Texte laufen sonst auseinander.
 */
export const KIND_LABELS: Record<AlertKind, string> = {
  server_unreachable: 'Server nicht erreichbar',
  disk_above: 'Festplatte über …',
  mem_above: 'RAM über …',
  cpu_above_for: 'CPU dauerhaft über …',
  process_not_running: 'Prozess läuft nicht',
  ssl_expires_in: 'Zertifikat läuft bald ab',
  updates_pending_above: 'Zu viele offene Updates',
}

export const alertChannelSchema = z.enum(['telegram', 'discord'])
export type AlertChannel = z.infer<typeof alertChannelSchema>

export const alertRuleSchema = z.object({
  id: z.string(),
  /** Null means every server. */
  serverId: z.string().nullable(),
  kind: alertKindSchema,
  /** Percent, count or days, depending on the kind. */
  threshold: z.number().nullable(),
  /** Only `cpu_above_for` uses this. */
  durationSec: z.number().int().positive().nullable(),
  /** Mount point, process name or domain, depending on the kind. */
  target: z.string().nullable(),
  cooldownSec: z.number().int().nonnegative(),
  channel: alertChannelSchema,
  enabled: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
})
export type AlertRule = z.infer<typeof alertRuleSchema>

/**
 * What each kind needs, so the form can ask for the right thing and the API can insist on it.
 *
 * A mount point is offered but not demanded: "any disk above 85 %" is the rule most people
 * actually want, and the evaluation then looks at the fullest one.
 */
export const ALERT_KIND_FIELDS: Record<
  AlertKind,
  {
    threshold: 'percent' | 'count' | 'days' | null
    target: 'mount' | 'process' | 'domain' | null
    targetRequired: boolean
    duration: boolean
  }
> = {
  server_unreachable: { threshold: null, target: null, targetRequired: false, duration: false },
  disk_above: { threshold: 'percent', target: 'mount', targetRequired: false, duration: false },
  mem_above: { threshold: 'percent', target: null, targetRequired: false, duration: false },
  cpu_above_for: { threshold: 'percent', target: null, targetRequired: false, duration: true },
  process_not_running: {
    threshold: null,
    target: 'process',
    targetRequired: true,
    duration: false,
  },
  ssl_expires_in: { threshold: 'days', target: 'domain', targetRequired: true, duration: false },
  updates_pending_above: {
    threshold: 'count',
    target: null,
    targetRequired: false,
    duration: false,
  },
}

export const createAlertRuleSchema = z
  .object({
    serverId: z.string().min(1).nullable().default(null),
    kind: alertKindSchema,
    threshold: z.number().nullable().default(null),
    durationSec: z.number().int().positive().nullable().default(null),
    target: z.string().trim().min(1).nullable().default(null),
    cooldownSec: z.number().int().min(0).max(86_400).default(900),
    channel: alertChannelSchema,
    enabled: z.boolean().default(true),
  })
  .superRefine((value, ctx) => {
    const needs = ALERT_KIND_FIELDS[value.kind]
    if (needs.threshold !== null && value.threshold === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['threshold'],
        message: 'Diese Regel braucht einen Wert.',
      })
    }
    if (needs.targetRequired && (value.target === null || value.target === '')) {
      ctx.addIssue({ code: 'custom', path: ['target'], message: 'Diese Regel braucht ein Ziel.' })
    }
    if (needs.duration && (value.durationSec === null || value.durationSec <= 0)) {
      ctx.addIssue({
        code: 'custom',
        path: ['durationSec'],
        message: 'Diese Regel braucht eine Dauer.',
      })
    }
    if (needs.target === 'process' && value.serverId === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['serverId'],
        message: 'Eine Prozessregel gilt für einen bestimmten Server.',
      })
    }
  })
export type CreateAlertRuleInput = z.input<typeof createAlertRuleSchema>

export const updateAlertRuleSchema = z.object({
  threshold: z.number().nullable().optional(),
  durationSec: z.number().int().positive().nullable().optional(),
  target: z.string().trim().min(1).nullable().optional(),
  cooldownSec: z.number().int().min(0).max(86_400).optional(),
  channel: alertChannelSchema.optional(),
  enabled: z.boolean().optional(),
})
export type UpdateAlertRuleInput = z.infer<typeof updateAlertRuleSchema>

export const alertRuleIdParamsSchema = z.object({ id: z.string().min(1) })

export const alertEventSchema = z.object({
  id: z.string(),
  ruleId: z.string(),
  serverId: z.string().nullable(),
  kind: alertKindSchema,
  firedAt: z.iso.datetime(),
  /** Set once the condition cleared; that is what the all-clear message is based on. */
  resolvedAt: z.iso.datetime().nullable(),
  message: z.string(),
  notified: z.boolean(),
})
export type AlertEvent = z.infer<typeof alertEventSchema>

export const alertEventsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  openOnly: z.stringbool().default(false),
})

// ---------------------------------------------------------------- channels

/** Never returns the secrets themselves — only whether a channel is set up (§6.1). */
export const channelStatusSchema = z.object({
  channel: alertChannelSchema,
  configured: z.boolean(),
  /** Something recognisable without being a secret: the chat id, or the webhook's host. */
  hint: z.string().nullable(),
})
export type ChannelStatus = z.infer<typeof channelStatusSchema>

export const telegramConfigSchema = z.object({
  channel: z.literal('telegram'),
  botToken: z.string().trim().min(10, 'Das sieht nicht nach einem Bot-Token aus.'),
  chatId: z.string().trim().min(1, 'Ohne Chat-ID weiß der Bot nicht, wohin.'),
})

export const discordConfigSchema = z.object({
  channel: z.literal('discord'),
  webhookUrl: z
    .string()
    .trim()
    .url('Das ist keine gültige URL.')
    .refine((url) => url.startsWith('https://'), { error: 'Der Webhook muss über HTTPS laufen.' }),
})

export const channelConfigSchema = z.discriminatedUnion('channel', [
  telegramConfigSchema,
  discordConfigSchema,
])
export type ChannelConfig = z.infer<typeof channelConfigSchema>

export const channelParamsSchema = z.object({ channel: alertChannelSchema })

export const channelTestResultSchema = z.object({
  ok: z.boolean(),
  message: z.string(),
})

// ---------------------------------------------------------------- poller

export const monitorStatusSchema = z.object({
  running: z.boolean(),
  /** How often a connected server is probed, in seconds. */
  intervalSec: z.number().int().positive(),
  /** The faster cadence for the server whose page is open (§5.8). */
  focusIntervalSec: z.number().int().positive(),
  /** Every n-th poll is written to the history. */
  storeEvery: z.number().int().positive(),
  /** Servers currently being polled. */
  watched: z.number().int().nonnegative(),
  /**
   * `desktop` means the poller only runs while the app is open, which the UI has to say out
   * loud rather than implying round-the-clock monitoring (§5.8).
   */
  shell: z.enum(['desktop', 'web']),
  lastRunAt: z.iso.datetime().nullable(),
})
export type MonitorStatus = z.infer<typeof monitorStatusSchema>
